/* A fixed id rather than `useId()`: this is a server component, and the two
 * places a second field could exist at once (a page and its own skeleton) are
 * never mounted together — and if they were, both would resolve to the same
 * tile and look identical. */
const PATTERN_ID = "miura-crease";

/**
 * THE CREASE FIELD (globals.css's `.sheet-field`) — every page in the record,
 * the dashboard included.
 *
 * A tiled Miura fold: two mountain creases and two valleys per 66×48 tile,
 * repeated behind the whole sheet. It once printed crease IDs ("M-04") in the
 * margins and every cell carried one in its corner; both went in 2026-10 when a
 * tester called the record "too busy".
 *
 * WHY A TILE AND NOT SIX LINES. The first build placed six fixed hairlines per
 * screen, reading "density behind body copy" as the reference world's
 * legibility failure. It is not the failure, because a `.sheet-cell` is opaque
 * --surface and spans ~92% of a phone's width — the field never reaches body
 * copy at all. It shows only in the gutters and the band under the nav, which
 * is where six fixed lines mostly landed under a card and a tile always lands.
 *
 * WHY A `<pattern>` AND NOT A CSS BACKGROUND. Both tile; only this one keeps
 * the stroke colours in the stylesheet, where the app's colour discipline
 * needs them (a data-URI background cannot hold a `var()`). The svg carries no
 * `viewBox`, so `userSpaceOnUse` is CSS pixels and the tile stays square at
 * every width — the thing the old `preserveAspectRatio="none"` viewBox could
 * not do, and the reason a stretched pattern was never an option before.
 *
 * Carries no information, so it is `aria-hidden` in full.
 */
export function CreaseField() {
  return (
    <div className="sheet-field" aria-hidden="true">
      <svg focusable="false">
        <defs>
          {/* One tile. The mountains are the zigzags; the valleys are the
              verticals that connect one fold to the next. Drawn 10px past the
              tile's top and bottom edges so the zigzag meets its own repeat
              instead of breaking at every seam.

              `fill="none"` IS AN ATTRIBUTE, NOT JUST THE CLASS RULE. An SVG
              path fills black by default, and a mountain path is a closed-ish
              wedge — so for however long the stylesheet has not arrived, the
              class alone leaves the whole sheet tiled in solid black chevrons.
              A presentation attribute is correct from the first paint and
              still loses to the stylesheet's own `fill`, so nothing about the
              styled result changes. The stroke stays CSS-only on purpose:
              those are tokens, and this is not. */}
          <pattern id={PATTERN_ID} width="66" height="48" patternUnits="userSpaceOnUse">
            <path className="crease-mountain" fill="none" d="M0 10 L33 0 L66 10" />
            <path className="crease-mountain" fill="none" d="M0 58 L33 48 L66 58" />
            <path className="crease-valley" fill="none" d="M0 10 L0 58" />
            <path className="crease-valley" fill="none" d="M33 0 L33 48" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${PATTERN_ID})`} />
      </svg>
    </div>
  );
}
